import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Trophy } from "lucide-react";
import api from "@/lib/api";
import { TRAITS, scoreColor } from "@/lib/traits";

const TRAIT_COLORS = ["#059669", "#0EA5E9", "#8B5CF6", "#F59E0B", "#EC4899", "#14B8A6", "#F43F5E"];

export default function Analytics() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/analytics").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="max-w-7xl mx-auto px-8 py-20 text-slate-400 font-mono">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
      <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-2">Insights</p>
      <h1 className="font-display text-5xl text-slate-900 mb-2" style={{ fontWeight: 900 }}>Leadership Analytics</h1>
      <p className="text-slate-500 mb-10">Aggregate trait benchmarks across departments and top-rated leaders.</p>

      <section className="bg-white border border-slate-200 rounded-2xl p-6 mb-8">
        <h2 className="font-display text-2xl text-slate-900 mb-4" style={{ fontWeight: 700 }}>Average trait score by department</h2>
        {data.department_benchmarks.length === 0 ? (
          <p className="text-slate-400 py-10 text-center">Not enough review data yet.</p>
        ) : (
          <div style={{ height: 380 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.department_benchmarks}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                <XAxis dataKey="department" tick={{ fontSize: 12, fill: "#64748B" }} />
                <YAxis domain={[0, 5]} tick={{ fontSize: 12, fill: "#64748B" }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {TRAITS.map((t, i) => (
                  <Bar key={t.key} dataKey={t.key} name={t.short} fill={TRAIT_COLORS[i]} radius={[3, 3, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      <section>
        <h2 className="font-display text-3xl text-slate-900 mb-5 flex items-center gap-2" style={{ fontWeight: 800 }}>
          <Trophy className="text-amber-500" /> Hall of Fame
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.top_leaders.map((s, i) => (
            <button
              key={s.id}
              onClick={() => navigate(`/supervisor/${s.id}`)}
              data-testid="hall-of-fame-card"
              className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:-translate-y-1 hover:shadow-xl hover:border-emerald-500/40 transition-all"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono text-3xl text-slate-200" style={{ fontWeight: 700 }}>#{i + 1}</span>
                <span className="font-display text-3xl" style={{ fontWeight: 900, color: scoreColor(s.overall) }}>
                  {s.overall.toFixed(1)}
                </span>
              </div>
              <div className="font-display text-xl text-slate-900 mt-2" style={{ fontWeight: 700 }}>{s.name}</div>
              <div className="text-sm text-slate-500">{s.title} · {s.company}</div>
              <div className="text-xs text-slate-400 mt-2 font-mono">{s.review_count} reviews · {s.recommend_rate}% recommend</div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
